import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import "./tracking-chart.css";
import CommonLoaderBlue from "../../../../../../../components/Loader/LoaderBlue";
import EmptyChartState from "../../EmptyStateScreens/ChartEmptyState/ChartEmptyState";

const TrackingChart = ({
  allEnrollmentsCount,
  allChartData,
  shortSelection,
  selectedRange,
}) => {
  const chartRef = useRef(null);
  const chartContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [remBase, setRemBase] = useState(
    parseFloat(getComputedStyle(document.documentElement).fontSize)
  );

  // Convert rem to px dynamically
  const getRemValue = (rem) => rem * remBase;

  useEffect(() => {
    const handleResize = () => {
      setRemBase(
        parseFloat(getComputedStyle(document.documentElement).fontSize)
      );
      if (chartRef.current) {
        chartRef.current.resize();
        if (allChartData && Object.keys(allChartData).length > 0) {
          fetchDataAndUpdateChart();
        }
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (chartRef.current) chartRef.current.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortSelection, allChartData, selectedRange]);

  const updateChart = (data) => {
    const fontSize = getRemValue(1.4); // 1.4rem ≈ 14px
    const labelLength = getRemValue(1.5); // 1.5rem ≈ 15px

    const option = {
      tooltip: {
        trigger: "item",
        formatter: "{a} <br/>{b} : {c} ({d}%)",
        backgroundColor: "rgba(0,0,0,.7)",
        textStyle: {
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize,
          color: "#fff",
        },
        padding: [
          getRemValue(0.2), // 0.2rem ≈ 2px
          getRemValue(1), // 1rem ≈ 10px
          getRemValue(0.2),
          getRemValue(1),
        ],
        position: (point) => {
          const offsetX = getRemValue(13.5); // ≈135px
          const offsetY = 0;
          return point[0] > offsetX
            ? [point[0] - getRemValue(16), point[1] - offsetY] // 160px
            : [point[0] - getRemValue(4), point[1] - offsetY]; // 40px
        },
      },
      series: [
        {
          name: "Tracking",
          type: "pie",
          radius: ["44%", "70%"],
          center: ["50%", "50%"],
          padAngle: 2,
          label: {
            fontFamily: "Inter",
            fontWeight: 400,
            fontSize,
            lineHeight: fontSize + getRemValue(0.2),
            formatter: (params) => `${params.name}\n${params.value}`,
            position: "outside",
            verticalAlign: "middle",
            show: false,
          },
          labelLine: {
            length: labelLength,
            length2: labelLength,
            maxSurfaceAngle: 90,
            show: false,
          },
          data,
          emphasis: {
            scale: false,
            itemStyle: {
              opacity: 1,
              borderRadius: getRemValue(0.4), // 0.4rem ≈ 4px
              shadowBlur: 0,
              shadowColor: "transparent",
              shadowOffsetX: 0,
              shadowOffsetY: 0,
              scale: false,
            },
          },
        },
      ],
      graphic: [
        {
          type: "text",
          left: "center",
          top: "44%",
          style: {
            text: "Total Enrollments ",
            textAlign: "center",
            fill: "#8B8BA7",
            fontSize: getRemValue(1), // 1rem ≈ 10px
            fontWeight: "700",
          },
        },
        {
          type: "text",
          left: "center",
          top: "49%",
          style: {
            text: allEnrollmentsCount,
            textAlign: "center",
            fill: "#414064",
            fontSize: getRemValue(3.2), // 3.2rem ≈ 32px
            fontWeight: "500",
            fontFamily: "Space Grotesk",
          },
        },
      ],
    };

    if (chartContainerRef.current) {
      chartRef.current = echarts.init(chartContainerRef.current, null, {
        renderer: "svg",
      });
      chartRef.current.setOption(option);
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  const fetchDataAndUpdateChart = () => {
    if (allChartData && Object.keys(allChartData).length > 0) {
      const dataforchart = [
        {
          name: "In Transit",
          value: allChartData.inTransit || 0,
          itemStyle: { color: "#1DBF73" },
        },
        {
          name: "Pending Dispatch",
          value: allChartData.inTransit || 0,
          itemStyle: { color: "#3FC8E4" },
        },
        {
          name: "Delivered",
          value: allChartData.delivered || 0,
          itemStyle: { color: "#FF9F0A" },
        },
      ];
      updateChart(dataforchart);
    }
  };

  useEffect(() => {
    if (allChartData && Object.keys(allChartData).length > 0) {
      fetchDataAndUpdateChart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortSelection, allChartData, selectedRange]);

  return (
    <div className="earning-chart-main">
      {!isLoading && allEnrollmentsCount === 0 ? (
        <EmptyChartState frominner={true} />
      ) : (
        <>
          <h1>Tracking</h1>
          {isLoading ? (
            <div className="flex flex-wrap flex-row justify-content-center align-items-center w-full h-full">
              <CommonLoaderBlue sizehere={20} color={"rgb(78, 105, 251)"} />
            </div>
          ) : null}

          <div
            style={{
              width: `${getRemValue(30) / remBase}rem`, // 30rem = responsive width
              marginLeft: `${getRemValue(-3.8) / remBase}rem`, // -3.8rem
            }}
            className="flex overflow-visible flex-wrap earning-chart-container-main flex-row justify-content-start"
          >
            <div ref={chartContainerRef} id="chartdiv"></div>
          </div>

          {!isLoading ? (
            <div className="w-full custom-legend">
              <div className="flex flex-wrap flex-row w-full justify-content-between">
                <div className="pending-dispatch-legend w-full flex flex-wrap flex-row justify-content-between">
                  <div className="flex flex-wrap flex-row justify-content-start align-items-center">
                    <div className="rectangle"></div>
                    <p>Pending Dispatch:</p>
                  </div>
                  <h6>{allChartData.inTransit || 0}</h6>
                </div>

                <div className="intransit-legend w-full flex flex-wrap flex-row justify-content-between">
                  <div className="flex flex-wrap flex-row justify-content-start align-items-center">
                    <div className="rectangle"></div>
                    <p>In Transit:</p>
                  </div>
                  <h6>{allChartData.inTransit || 0}</h6>
                </div>

                <div className="delivered-legend w-full flex flex-wrap flex-row justify-content-between">
                  <div className="flex flex-wrap flex-row justify-content-start align-items-center">
                    <div className="rectangle"></div>
                    <p>Delivered:</p>
                  </div>
                  <h6>{allChartData.delivered || 0}</h6>
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default TrackingChart;
